import { type FC, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";

import { AccountRow } from "@/pages/postloss/modules/components/AccountRow";
import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { buildMortgageSubtitle } from "./debtSubtitles";

export interface ExtractedMortgageProps extends FlowCustomComponentProps {
  mortgageId: number | null;
  isFirst: boolean;
}

export const ExtractedMortgage: FC<ExtractedMortgageProps> = ({
  mortgageId,
  isFirst,
  goToScreen,
  answers,
  setAnswer,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { debts, assets, isLoading } = useEstateItems(estateId);

  const allMortgages = filterByCategory(debts, "Secured debts");
  const mortgage = mortgageId
    ? (allMortgages.find((m) => m.id === mortgageId) ?? null)
    : (allMortgages[0] ?? null);
  const properties = filterByCategory(assets, "Properties");

  // Tracks whether mortgages were ever found in this mount — prevents spurious
  // redirect if a mortgage is deleted after being found.
  const itemsEverFound = useRef(false);

  useEffect(() => {
    if (isLoading) return;

    if (allMortgages.length > 0) {
      itemsEverFound.current = true;

      if (mortgage && String(mortgage.id) !== String(answers["prefill_mortgage_id"])) {
        setAnswer("prefill_mortgage_id", String(mortgage.id));
        setAnswer("prefill_lender_name", mortgage.item_name || "");
        setAnswer("prefill_balance", mortgage.balance ? String(mortgage.balance) : "");
        const mortgageMeta = mortgage.metadata as Record<string, unknown> | null;
        setAnswer("prefill_property_address", (mortgageMeta?.property_address as string) || "");
      }
    } else if (isFirst && !itemsEverFound.current && answers["extracted_mortgages"] !== "none") {
      const firstPropertyScreen =
        properties.length > 0
          ? `property_loans_question_${properties[0].id}`
          : "has_mortgage_no_property";
      goToScreen(firstPropertyScreen, {
        ...answers,
        extracted_mortgages: "none",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allMortgages.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  // No mortgages — effect will redirect. Return null to avoid a persistent
  // spinner in case the redirect is delayed or the user navigates back here.
  if (allMortgages.length === 0) {
    return null;
  }

  if (!mortgage) return null;

  const mortgageMeta = mortgage.metadata as Record<string, unknown> | null;

  return (
    <div className="flex flex-col gap-6">
      <AccountRow
        item={{
          ...mortgage,
          item_name: (mortgageMeta?.property_address as string) || "Property address unknown",
        }}
        subtitle={buildMortgageSubtitle(mortgage)}
      />
      <p className="text-sm font-medium">Do you have any documents related to this?</p>
    </div>
  );
};
